// src/pages/student/StudentSettings.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Card, Button, Spinner, Row, Col, Form, InputGroup, Dropdown
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { useBrand, hexToRgba } from "./_brand";
import {
  doc, getDoc, setDoc, serverTimestamp
} from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../services/firebase";

/* ---------------- utils ---------------- */
const norm = (s) => String(s || "").trim().toLowerCase();

const ACCENTS = [
  "#0d6efd","#3b82f6","#1d4ed8","#60a5fa",
  "#dc3545","#ef4444","#991b1b","#f87171",
  "#198754","#22c55e","#065f46","#86efac",
  "#ffc107","#facc15","#d97706","#fde047",
  "#6f42c1","#8b5cf6","#7e22ce","#c4b5fd",
  "#20c997","#06b6d4","#164e63","#67e8f9",
  "#fd7e14","#f97316","#9a3412","#fb923c",
  "#6c757d","#94a3b8","#334155","#e2e8f0",
  "#000000","#ffffff",
];
const normalizeHex6 = (hex) => {
  let v = String(hex || "").trim().replace(/^#/, "");
  if (v.length === 3) v = v.split("").map((x) => x + x).join("");
  return /^[0-9a-f]{6}$/i.test(v) ? `#${v.toLowerCase()}` : "#3b82f6";
};

/* ---------------- small UI ---------------- */
function AccentDropdown({ active, onPick }) {
  const act = normalizeHex6(active);
  return (
    <Dropdown>
      
      <Dropdown.Menu style={{ padding: 10, maxHeight: 240, overflowY: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 24px)", gap: 8, minWidth: 230 }}>
          {ACCENTS.map((c) => {
            const col = normalizeHex6(c);
            return (
              <button
                key={col}
                type="button"
                title={col}
                onClick={() => onPick && onPick(col)}
                style={{
                  width: 20, height: 20, borderRadius: "50%", background: col,
                  border: "none", outline: "none", cursor: "pointer",
                  transition: "box-shadow .2s, transform .15s",
                  boxShadow: act === col
                    ? `0 0 0 2px rgba(0,0,0,.35), 0 0 0 4px ${hexToRgba(col, .4)}`
                    : `0 0 0 1px rgba(0,0,0,.2)`,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              />
            );
          })}
          <label
            style={{
              width: 20, height: 20, borderRadius: "50%", cursor: "pointer",
              border: "1px dashed rgba(0,0,0,.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, color: "#666",
            }}
            title="Custom color"
          >
            +
            <input type="color" style={{ display: "none" }} onChange={(e) => onPick && onPick(e.target.value)} />
          </label>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default function StudentSettings() {
  const brand = useBrand();
  const { primary, accent, setPrimary, setAccent } = brand;

  // Derived theme values (and expose CSS vars)
  const primaryColor = useMemo(() => normalizeHex6(primary || "#3b82f6"), [primary]);
  const accentComputed = useMemo(() => normalizeHex6(accent || primaryColor), [accent, primaryColor]);
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", primaryColor);
    root.style.setProperty("--brand-accent", accentComputed);
  }, [primaryColor, accentComputed]);

  // Session
  const [schoolId] = useState(() => localStorage.getItem("schoolId"));
  const [studentId] = useState(() => localStorage.getItem("studentId"));
  const [studentName] = useState(() => localStorage.getItem("studentName") || "");

  // Profile
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Editable fields
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [removePhoto, setRemovePhoto] = useState(false);

  const fileRef = useRef(null);

  const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);

  // Load student profile
  useEffect(() => {
    const load = async () => {
      if (!schoolId || !studentId) {
        toast.error("Missing student session. Please sign in again.");
        setLoading(false);
        return;
      }
      try {
        const ref = doc(db, "schools", schoolId, "students", studentId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          toast.error("Could not find your student profile.");
          setProfile(null);
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        setProfile(data);
        setPhone(data.phone || "");
        setAddress(data.address || "");
        setPhotoPreview(data.photoURL || "");
        setRemovePhoto(false);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [schoolId, studentId]);

  const isActive = norm(profile?.status) !== "inactive";

  // Photo preview
  useEffect(() => {
    if (!photoFile) return;
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    setRemovePhoto(false);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  // Simple validators
  const digits = (s) => (s || "").replace(/[^\d+]/g, "");
  const isPhoneValid = useMemo(() => {
    const p = phone.trim();
    if (!p) return true; // optional
    const clean = p.replace(/\s+/g, "");
    return /^(\+?\d{7,15})$/.test(clean);
  }, [phone]);

  const uploadPhotoIfNeeded = async () => {
    // Remove requested
    if (removePhoto) return "";

    // No new file, keep existing
    if (!photoFile) return profile?.photoURL || "";

    // Upload new
    const safeName = `photo_${Date.now()}_${photoFile.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
    const path = `schools/${schoolId}/students/${studentId}/${safeName}`;
    const r = sRef(storage, path);
    await uploadBytes(r, photoFile);
    return await getDownloadURL(r);
  };

  const deletePrevPhotoIfReplaced = async (newUrl) => {
    try {
      const prev = profile?.photoURL;
      if (!prev) return;
      if (!newUrl || newUrl !== prev) {
        // try to infer path; only delete if it was in our bucket path
        const matches = prev.match(/\/o\/(.+)\?/); // gs path inside URL
        if (matches && matches[1]) {
          const decoded = decodeURIComponent(matches[1]);
          const r = sRef(storage, decoded);
          await deleteObject(r).catch(() => {});
        }
      }
    } catch {}
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!profile || !schoolId || !studentId) return;
    if (!isActive) {
      toast.info("Your account is inactive. Updates are disabled.");
      return;
    }
    if (!isPhoneValid) {
      toast.error("Please enter a valid phone number (7–15 digits, optional +).");
      return;
    }
    try {
      setSaving(true);
      const photoURL = await uploadPhotoIfNeeded();

      // Only write allowed fields (phone, address, photoURL, updatedAt)
      const ref = doc(db, "schools", schoolId, "students", studentId);
      await setDoc(
        ref,
        {
          phone: digits(phone).trim() || "",
          address: (address || "").trim(),
          photoURL: photoURL || "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // If changed, attempt cleanup of previous
      await deletePrevPhotoIfReplaced(photoURL);

      // refresh local profile
      setProfile((p) => ({ ...p, phone: digits(phone).trim() || "", address: (address || "").trim(), photoURL }));
      toast.success("Profile updated.");
      setPhotoFile(null);
      setRemovePhoto(false);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      console.error(e);
      const msg = e?.message?.includes("Missing or insufficient permissions")
        ? "Permissions error: your account may not be allowed to edit these fields."
        : "Failed to save changes.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const resetEdits = () => {
    setPhone(profile?.phone || "");
    setAddress(profile?.address || "");
    setPhotoFile(null);
    setPhotoPreview(profile?.photoURL || "");
    setRemovePhoto(false);
    fileRef.current && (fileRef.current.value = "");
  };

  const removePhotoNow = () => {
    setPhotoFile(null);
    setPhotoPreview("");
    setRemovePhoto(true);
    if (fileRef.current) fileRef.current.value = "";
  };

  const pickTheme = (c) => {
    const col = normalizeHex6(c);
    setPrimary?.(col);
    setAccent?.(col);
  };

  return (
    <div className="position-relative overflow-hidden">
      {/* Background */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{
          zIndex: 0,
          background: `
            radial-gradient(1100px 600px at 8% 12%, ${hexToRgba(primaryColor, .10)} 0%, transparent 60%),
            radial-gradient(1000px 560px at 92% 88%, ${hexToRgba(accentComputed,  .10)} 0%, transparent 60%)
          `,
        }}
      />
      <Particles
        id="student-settings-bg-particles"
        className="position-fixed top-0 start-0 w-100 h-100"
        init={particlesInit}
        style={{ zIndex: 1, pointerEvents: "none" }}
        options={{
          fpsLimit: 60,
          fullScreen: { enable: false },
          background: { color: { value: "transparent" } },
          detectRetina: true,
          interactivity: {
            events: { onHover: { enable: true, mode: "repulse" }, onClick: { enable: true, mode: "push" }, resize: true },
            modes: { repulse: { distance: 120, duration: 0.35 }, push: { quantity: 2 } },
          },
          particles: {
            number: { value: 60, density: { enable: true, area: 900 } },
            color: { value: [primaryColor, accentComputed] },
            links: { enable: true, color: hexToRgba(primaryColor, 0.45), distance: 140, opacity: 0.45, width: 1 },
            move: { enable: true, speed: 1.0, outModes: { default: "out" } },
            opacity: { value: 0.55 },
            size: { value: { min: 1, max: 3 } },
          },
          responsive: [
            { maxWidth: 768, options: { particles: { number: { value: 36 } } } },
            { maxWidth: 420, options: { particles: { number: { value: 20 } } } },
          ],
        }}
      />

      {/* Foreground */}
      <div className="p-2 p-sm-3 position-relative" style={{ zIndex: 2 }}>
        <ToastContainer
          position="bottom-right"
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          theme="dark"
        />

        <Row className="g-3">
          <Col xs={12} lg={4}>
            <Card className="border-0 shadow-sm">
              <Card.Header className=" d-flex justify-content-between align-items-center">
                <span className="fw-semibold">Profile</span>
                <AccentDropdown active={primaryColor} onPick={pickTheme} />
              </Card.Header>
              <Card.Body className="d-flex flex-column align-items-center">
                <div className={`avatar-wrap mb-3 ${!photoPreview ? "empty" : ""}`}>
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt={studentName || "Student"}
                      className="avatar-img"
                    />
                  ) : (
                    <div className="avatar-fallback">
                      <i className="bi bi-person-fill" />
                    </div>
                  )}
                </div>
                <div className="w-100">
                  <Form.Group className="mb-2">
                    <Form.Label>Profile photo</Form.Label>
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                      <Form.Control
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        disabled={!isActive || loading}
                        onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                      />
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={removePhotoNow}
                        disabled={(!profile?.photoURL && !photoPreview) || !isActive || loading}
                      >
                        <i className="bi bi-trash me-1" />
                        Remove
                      </Button>
                    </div>
                    <Form.Text className="text-muted">Recommended: square image, ≤1MB.</Form.Text>
                  </Form.Group>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={8}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-0">Student Settings</h5>
                    <small className="text-muted">
                      {studentName || "Student"} • {profile?.className || "—"}{profile?.section ? `-${profile.section}` : ""} • {norm(profile?.status) === "inactive" ? "Inactive" : "Active"}
                    </small>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" /> Loading profile…
                  </div>
                ) : (
                  <Form onSubmit={save}>
                    {/* Read-only identity block */}
                    <Row className="g-2 mb-2">
                      <Col md={6}>
                        <Form.Label className="mb-1">Name</Form.Label>
                        <Form.Control value={profile?.name || profile?.displayName || "—"} disabled />
                      </Col>
                      <Col md={3}>
                        <Form.Label className="mb-1">Class</Form.Label>
                        <Form.Control value={profile?.className || "—"} disabled />
                      </Col>
                      <Col md={3}>
                        <Form.Label className="mb-1">Section</Form.Label>
                        <Form.Control value={profile?.section || (Array.isArray(profile?.sections) ? profile.sections.join(",") : "—")} disabled />
                      </Col>
                    </Row>

                    <Row className="g-2 mb-2">
                      <Col md={3}>
                        <Form.Label className="mb-1">Roll No</Form.Label>
                        <Form.Control value={profile?.rollNo || "—"} disabled />
                      </Col>
                      <Col md={9}>
                        <Form.Label className="mb-1">Email</Form.Label>
                        <Form.Control value={profile?.email || profile?.parentEmail || "—"} disabled />
                      </Col>
                    </Row>

                    {/* Editable fields allowed by rules */}
                    <Row className="g-2 mt-2">
                      <Col md={6}>
                        <Form.Label className="mb-1">Phone</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="bg-light"><i className="bi bi-telephone" /></InputGroup.Text>
                          <Form.Control
                            placeholder="Your phone number"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            disabled={!isActive}
                            isInvalid={!isPhoneValid}
                          />
                          <Form.Control.Feedback type="invalid">
                            Enter 7–15 digits (you may start with +).
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Col>
                      <Col md={6}>
                        <Form.Label className="mb-1">Address</Form.Label>
                        <Form.Control
                          placeholder="Your full address"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          disabled={!isActive}
                          maxLength={300}
                        />
                        <div className="form-text text-end">{(address || "").length}/300</div>
                      </Col>
                    </Row>

                    <div className="mt-4 d-flex gap-2">
                      <Button
                        type="submit"
                        className="btn-neo"
                        disabled={!isActive || saving || !isPhoneValid}
                        title={!isPhoneValid ? "Fix phone number" : ""}
                      >
                        <span className="btn-inner">
                          {saving && <span className="btn-spinner" aria-hidden="true" />}
                          {saving ? "Saving…" : "Save changes"}
                        </span>
                      </Button>
                      <Button variant="outline-secondary" type="button" onClick={resetEdits} disabled={saving}>
                        Reset
                      </Button>
                    </div>

                    <div className="mt-3 small text-muted">
                      Last updated: <b>{profile?.updatedAt?.toDate ? dayjs(profile.updatedAt.toDate()).format("DD MMM YYYY, h:mm A") : "—"}</b>
                    </div>

                    {norm(profile?.status) === "inactive" ? (
                      <div className="mt-3 alert alert-warning mb-0">
                        Your account is <b>inactive</b>. You can view details but cannot save changes. Please contact your school management.
                      </div>
                    ) : null}
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Styles */}
      <style>{`
        :root{
          --brand-primary: ${primaryColor};
          --brand-accent: ${accentComputed};
        }

        /* Toastify themed to accent/primary */
        .Toastify__toast {
          border-radius: 10px;
          font-size: 0.9rem;
          border: 1px solid ${hexToRgba(accentComputed, 0.4)};
          background: linear-gradient(
            135deg,
            ${hexToRgba(primaryColor, 0.14)} 0%,
            ${hexToRgba(accentComputed, 0.14)} 100%
          );
          backdrop-filter: blur(6px);
        }
        .Toastify__toast-body { color: ${accentComputed}; font-weight: 500; }
        .Toastify__progress-bar { background: ${accentComputed}; }

        .avatar-wrap {
          width: 140px; height: 140px;
          border-radius: 50%;
          background: ${hexToRgba(primaryColor, 0.14)};
          border: 2px solid ${hexToRgba(primaryColor, 0.28)};
          display: grid; place-items: center;
          overflow: hidden;
          position: relative;
        }
        .avatar-wrap.empty::after{
          content: "No photo";
          position: absolute; bottom: 8px; left: 50%; transform: translateX(-50%);
          font-size: 11px; color: rgba(0,0,0,.55);
          background: rgba(255,255,255,.6);
          padding: 2px 6px; border-radius: 8px;
        }
        .avatar-img { width: 100%; height: 100%; object-fit: cover; }
        .avatar-fallback { font-size: 64px; color: ${primaryColor}; display: grid; place-items: center; width: 100%; height: 100%; }

        /* primary CTA (same look as Support page) */
        .btn-neo{
          --_bg1: ${hexToRgba(primaryColor, .95)};
          --_bg2: ${hexToRgba(accentComputed, .95)};
          color:#fff !important;
          background: linear-gradient(135deg, var(--_bg1) 0%, var(--_bg2) 100%);
          border: 1px solid ${hexToRgba(primaryColor,.45)};
          box-shadow: 0 10px 18px ${hexToRgba(primaryColor,.24)};
          transition: transform .12s ease, box-shadow .12s ease, filter .12s ease;
          position: relative;
          overflow: hidden;
        }
        .btn-neo .btn-inner{ position: relative; z-index: 1; }
        .btn-neo .btn-spinner{
          width:14px; height:14px; border-radius:50%;
          border:2px solid #fff; border-top-color: transparent; display:inline-block; margin-right:8px;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .btn-neo:hover{ transform: translateY(-1px); filter: brightness(1.04); box-shadow: 0 14px 26px ${hexToRgba(primaryColor,.30)}; }
        .btn-neo:active{ transform: translateY(0); filter: brightness(.98); box-shadow: 0 6px 14px ${hexToRgba(primaryColor,.24)} inset; }
        .btn-neo.disabled, .btn-neo:disabled { opacity: .65 !important; filter: grayscale(.2); box-shadow: none; }
      `}</style>
    </div>
  );
}
